import { FastColor } from "@ant-design/fast-color";
import { ThemeName } from "@dto/usersResponse.dto";
import { theme, type ThemeConfig } from "antd";
import { AliasToken, SeedToken } from "antd/es/theme/interface";

type XmComponentsToken = NonNullable<ThemeConfig['components']>;

export const createHoverColor = (color: string): string => {
  return `color-mix(in hsl, ${color} 80%, white)`;
};

interface ColorVariables {
  /* *** Main *** */
  "xm-color-primary"?: string; // Logo, titles, card headers
  "xm-color-primary-text"?: string;
  "xm-color-primary-hover"?: string;
  "xm-color-secondary"?: string; // Primary CTA, active states
  "xm-color-secondary-text"?: string;
  "xm-color-secondary-hover"?: string;
  "xm-color-tertiary"?: string; // Icons - probably remove
  "xm-color-highlight"?: string;

  /* States */
  "xm-color-info"?: string;
  "xm-color-error"?: string;
  "xm-color-warning"?: string;

  /* *** General *** */
  "xm-color-text"?: string;
  "xm-color-text-oposite"?: string;
  "xm-color-scroll"?: string;
  "xm-bg-color-page"?: string;
  "xm-bg-color-base"?: string;
  "xm-bg-color-light"?: string;
  "xm-bg-color-table-header"?: string;
  "xm-bg-secondary"?: string;
  "xm-border-color"?: string;

  /* Size */
  "xm-font-family"?: string;
  "xm-font-weight-medium"?: string;

  /* *** Login *** */
  "xm-login-bg-color"?: string;
  "xm-login-form-bg-color"?: string;

  /* *** Menu *** */
  "xm-logo-bg-color"?: string;
  "xm-logo-color"?: string;
  "xm-logo-separator-bg-color"?: string;

  "xm-menu-top-bg-color"?: string;
  "xm-menu-top-color"?: string;
  "xm-menu-top-underline-color"?: string;

  "xm-menu-left-bg-color"?: string;
  "xm-menu-left-color"?: string;
  "xm-menu-left-selected-bg-color"?: string;
  "xm-menu-left-selected-color"?: string;
  "xm-menu-left-hover-bg-color"?: string;
  "xm-menu-left-hover-color"?: string;
  "xm-menu-left-scroll-color"?: string;

  /* *** Dashboard *** */
  "xm-dashboard-header-bg-color"?: string;
  "xm-dahsboard-header-color"?: string;
  "xm-dashboard-body-bg-color"?: string;

  /* *** Range picker *** */
  "xm-picker-candidate-bg-color"?: string;
  "xm-picker-dropped-bg-color"?: string;

  /* *** Xbot *** */
  "xm-xbot-header-bg-color": string;
  "xm-xbot-body-bg-color": string;
  "xm-xbot-border-color": string;

  /* *** Miscellaneous *** */
  "xm-fav-color"?: string;
}

// resolved once on <html>: a --ant-* ref gives the global token, not a component's local override
const xmBaseVars: ColorVariables = {
  /* *** Main *** */
  "xm-color-highlight": '#48a',
  /* States */
  "xm-color-info": "#17a2b8",
  "xm-color-error": "#b00",
  "xm-color-warning": "chocolate",

  /* *** General *** */
  "xm-color-text": "#000",
  "xm-color-text-oposite": "#cdd",
  "xm-color-scroll": "#c4c4c9",
  "xm-bg-color-page": "var(--ant-color-bg-base)",

  "xm-bg-color-base": "color-mix(in hsl, var(--xm-color-primary) 30%, white)",
  "xm-bg-color-light": "color-mix(in hsl, var(--xm-bg-color-base) 94%, white)",
  "xm-bg-color-table-header": "color-mix(in hsl, var(--xm-bg-color-base) 96%, white)",
  "xm-bg-secondary": "var(--ant-color-bg-container)",

  "xm-border-color": "#dddddd",
  /* Size */
  "xm-font-family": "-apple-system, BlinkMacSystemFont, 'Roboto', 'Segoe UI', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
  "xm-font-weight-medium": "600", // 500 looks in Firefox like 400

  /* *** Login *** */
  "xm-login-bg-color": "var(--xm-logo-bg-color)",
  "xm-login-form-bg-color": "var(--ant-color-bg-container)",

  /* *** Range picker *** */
  "xm-picker-candidate-bg-color": "color-mix(in srgb, var(--xm-color-secondary) 12%, transparent)",
  "xm-picker-dropped-bg-color": "color-mix(in srgb, #888 30%, var(--ant-color-bg-elevated))",
  /* *** Xbot *** */
  "xm-xbot-header-bg-color": "#f6fbff",
  "xm-xbot-body-bg-color": "#ffffff",
  "xm-xbot-border-color": "#d1dbe3",

  /* *** Miscellaneous *** */
  "xm-fav-color": "rgba(222, 200, 0, 0.7)"
};

const fontSize = 12;

const xmSeedToken: Partial<SeedToken> = {
  // named status colours of the light themes; makeDarkToken swaps them for antd's seeds
  colorSuccess: 'darkgreen',
  colorError: 'darkred',
  borderRadius: 4,
  fontSize,
  controlHeight: 28,
  motionUnit: 0.05,
  sizeStep: 2,
};

const xmCommonToken: Partial<AliasToken> = {
  ...xmSeedToken,
  controlOutlineWidth: 1,
  fontSizeSM: fontSize,
  fontSizeHeading1: 2 * fontSize,
  fontSizeHeading2: 1.75 * fontSize,
  fontSizeHeading3: 1.5 * fontSize,
  fontSizeHeading4: 1.25 * fontSize,
  fontSizeHeading5: 1.125 * fontSize,
  fontWeightStrong: Number(xmBaseVars['xm-font-weight-medium']),
  sizeXXL: 16,
  sizeXL: 14,
  sizeLG: 12,
  sizeMD: 12,
  sizeMS: 10,
  size: 8,
  sizeSM: 6,
  sizeXS: 4,
  sizeXXS: 2,
};

const xmBlueVars: ColorVariables = {
  ...xmBaseVars,
  "xm-color-primary": "#002447",
  "xm-color-primary-text": "#cdd",
  "xm-color-secondary": "#094b94",
  "xm-color-secondary-text": "#f2fffa",
  "xm-color-secondary-hover": createHoverColor("var(--xm-color-secondary)"),
  "xm-color-tertiary": "#002447",
  "xm-color-highlight": "#094b94",
  "xm-color-scroll": "#95b0d0",

  "xm-bg-color-base": "#f5f7f8", // Dashboard bg
  "xm-bg-color-light": "#f9f9f9", // Exporter - form
  "xm-bg-color-table-header": "#f0f5f8", // State Timeline

  /* *** Menu *** */
  "xm-logo-bg-color": "#002447",
  "xm-logo-color": "#ccdad8",
  // Top
  "xm-menu-top-bg-color": "#002447",
  "xm-menu-top-color": "#ccdad8",
  "xm-menu-top-underline-color": "#c7dbf1",
  // Left
  "xm-menu-left-bg-color": "linear-gradient(135deg, #f2f7fc 0%, #f5f8fc 100%)",
  "xm-menu-left-color": "#333344",
  "xm-menu-left-selected-bg-color": "#e3ecfc",
  "xm-menu-left-selected-color": "#333344",
  "xm-menu-left-hover-bg-color": "#e3ecfc82",
  "xm-menu-left-scroll-color": "#c4c4c9",

  /* *** Dashboard *** */
  "xm-dashboard-header-bg-color": "#e3ecfc",
  "xm-dahsboard-header-color": "#002447",
  "xm-dashboard-body-bg-color": "",
};

const xmDarkVars: ColorVariables = {
  ...xmBaseVars,
  "xm-color-primary": "#444",
  "xm-color-secondary": "#4488aa",
  "xm-color-secondary-text": "#ededed",
  "xm-color-secondary-hover": createHoverColor("var(--xm-color-secondary)"),
  "xm-color-tertiary": "#555555", // Dashboard group title
  "xm-color-text": "#fff",
  "xm-bg-color-page": "#222",
  "xm-color-scroll": "#303030",
  "xm-logo-separator-bg-color": "#333",
  "xm-border-color": "#222",

  "xm-bg-color-base": "#333333", // Dashboard bg
  "xm-bg-color-light": "#2a2a2a", // Exporter - form
  "xm-bg-color-table-header": "#333", // State Timeline
  "xm-bg-secondary": "#333", // Server - Graph

  /* *** Menu *** */
  "xm-logo-bg-color": "#333",
  "xm-logo-color": "#ccdddd",
  // Top
  "xm-menu-top-bg-color": "#333",
  "xm-menu-top-color": "#ccdddd",
  // Left
  "xm-menu-left-bg-color": "#333",
  "xm-menu-left-color": "#ccdddd",
  "xm-menu-left-selected-bg-color": "#555555",
  "xm-menu-left-selected-color": "#ccdddd",
  "xm-menu-left-hover-bg-color": "#444",
  "xm-menu-left-scroll-color": "#5a5959",

  /* *** Range picker *** */
  "xm-picker-candidate-bg-color": "color-mix(in srgb, var(--xm-color-secondary) 30%, transparent)",
  "xm-picker-dropped-bg-color": "color-mix(in srgb, #fff 12%, var(--ant-color-bg-elevated))",
  /* *** Xbot *** */
  "xm-xbot-header-bg-color": "#333",
  "xm-xbot-body-bg-color": "#222",
  "xm-xbot-border-color": "#444",

  /* *** Dashboard *** */
  "xm-dashboard-header-bg-color": "",
  "xm-dahsboard-header-color": "",
  "xm-dashboard-body-bg-color": "",
};

const xmLightVars: ColorVariables = {
  ...xmBaseVars,
  "xm-color-primary": "#dee2e4",
  "xm-color-primary-text": "#333344",
  "xm-color-secondary": "#2c6094",
  "xm-color-secondary-text": "#f2fffa",
  "xm-color-secondary-hover": createHoverColor("var(--xm-color-secondary)"),
  "xm-color-tertiary": "#b8bcbd",
  "xm-color-highlight": "#2c6094",

  "xm-bg-color-base": "#f5f5f6", // Dashboard bg
  "xm-bg-color-light": "#f9f9f9", // Exporter - form
  "xm-bg-color-table-header": "#f0f5f8", // State Timeline

  /* *** Menu *** */
  "xm-logo-bg-color": "#e9ecf0",
  "xm-logo-color": "#333344",
  // Top
  "xm-menu-top-bg-color": "#e9ecf0",
  "xm-menu-top-color": "#333344",
  // Left
  "xm-menu-left-bg-color": "#f4f5f7",
  "xm-menu-left-color": "#333344",
  "xm-menu-left-selected-bg-color": "#dee2e4",
  "xm-menu-left-selected-color": "#333344",
  "xm-menu-left-hover-bg-color": "#e4e7e8",
  "xm-menu-left-scroll-color": "#c4c4c9",
  "xm-logo-separator-bg-color": "#e9ecf0",

  /* *** Dashboard *** */
  "xm-dashboard-header-bg-color": "#dee2e4",
  "xm-dahsboard-header-color": "#333344",

  /* *** Login *** */
  "xm-login-bg-color": "#f4f5f7",
};

const xmOriginalVars: ColorVariables = {
  ...xmBaseVars,
  "xm-color-primary": "#2b4f60",
  "xm-color-primary-text": "#ededed",
  "xm-color-secondary": "#3c6c84",
  "xm-color-secondary-text": "#ededed",
  "xm-color-secondary-hover": createHoverColor("var(--xm-color-secondary)"),
  "xm-color-tertiary": "#3c6c84",

  "xm-bg-color-base": "#e2ecf2", // Dashboard bg
  "xm-bg-color-light": "#f7fafb", // Exporter - form
  "xm-bg-color-table-header": "#f0f5f8", // State Timeline

  "xm-color-scroll": "var(--xm-color-secondary)",

  /* *** Menu *** */
  "xm-logo-bg-color": "#2b4f60",
  "xm-logo-color": "#ccdddd",
  // Top
  "xm-menu-top-bg-color": "#f0f5f8",
  "xm-menu-top-color": "#000",
  // Left
  "xm-menu-left-bg-color": "#233f4d",
  "xm-menu-left-color": "#ccdddd",
  "xm-menu-left-selected-bg-color": "#3c6c84",
  "xm-menu-left-selected-color": "#ccdddd",
  "xm-menu-left-hover-bg-color": "#2b4f60",
  "xm-menu-left-scroll-color": "#c4c4c9",

  /* *** Dashboard *** */
  "xm-dashboard-header-bg-color": "",
  "xm-dahsboard-header-color": "",
  "xm-dashboard-body-bg-color": "",
};

// xm vars passed to antd tokens must be plain colors, not var(): antd computes derived colors from them
function makeLightToken(vars: ColorVariables): Partial<AliasToken> {
  const bg = new FastColor(vars["xm-color-secondary"]).lighten(40);
  return {
    ...xmCommonToken,
    colorLink: vars["xm-color-secondary"],
    colorLinkHover: vars["xm-color-highlight"],
    colorPrimary: vars["xm-color-secondary"],
    colorPrimaryBg: bg.lighten(6).toHexString(),
    colorPrimaryBgHover: vars["xm-color-highlight"],
    colorWarning: vars["xm-color-warning"],
    controlItemBgActive: bg.lighten(4).toHexString(),
    controlItemBgActiveHover: bg.lighten(2).toHexString(),
    controlItemBgHover: bg.toHexString(),
    colorBgBase: "#fafafa",
    colorTextBase: "#17191c",
    colorBgContainer: "#fff"
  };
}

function makeDarkToken(vars: ColorVariables): Partial<AliasToken> {
  return {
    ...xmCommonToken,
    // the dark algorithm darkens the seeds further; antd's own seeds give readable shades on dark backgrounds
    colorSuccess: theme.defaultSeed.colorSuccess,
    colorError: theme.defaultSeed.colorError,
    colorLink: vars["xm-color-secondary"],
    colorLinkHover: vars["xm-color-highlight"],
    colorPrimary: vars["xm-color-secondary"],
    colorWarning: vars["xm-color-warning"],
    colorBgBase: "#1a1a1a",
    colorTextBase: "#ffffff",
    colorBgContainer: "#444444"
  };
}

export interface ThemeVars {
  name: ThemeName;
  xmVars: ColorVariables;
  token: Partial<AliasToken>;
  components?: XmComponentsToken;
}

export const THEMES: Record<ThemeName, ThemeVars> = {
  blue: { name: 'blue', xmVars: xmBlueVars, token: makeLightToken(xmBlueVars) },
  dark: { name: 'dark', xmVars: xmDarkVars, token: makeDarkToken(xmDarkVars), components: { DatePicker: { cellHoverBg: '#33586b' } } },
  light: { name: 'light', xmVars: xmLightVars, token: makeLightToken(xmLightVars) },
  original: { name: 'original', xmVars: xmOriginalVars, token: makeLightToken(xmOriginalVars) },
};

export function applyXmCssVars(themeVars: ThemeVars): void {
  const root = document.documentElement;
  // collect first: removing while iterating the live declaration skips entries
  const toRemove = Array.from(root.style).filter(prop => prop.startsWith('--xm-'));
  toRemove.forEach(prop => root.style.removeProperty(prop));
  for (const [key, value] of Object.entries(themeVars.xmVars)) {
    root.style.setProperty(`--${key}`, value);
  }
}
